import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import Tabs from "@/common/components/Tabs";
import { tabsDefaultWithFlexGrowClassName } from "@/common/constants/tabsDefaultClassName.constant";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { maintenanceWorkflowActions } from "../../constants/maintenanceWorkflowActions.constant";
import { useMaintenanceWorkflow } from "../../hooks/useMaintenanceWorkflow";
import { useMaintenanceWorkflowForwardingsDirectiveEntryKlasses } from "../../hooks/useMaintenanceWorkflowForwardingsDirectiveEntryKlasses";
import { useWorkflowShowStore } from "../../stores/workflowShowStore";
import { getWorkflowTabsList } from "../../utils/getWorkflowTabsList";
import MaintenanceWorkflowSidebar from "./components/MaintenanceWorkflowSidebar";
import MaintenanceWorkflowForwardingShowLoading from "./components/tabs/ForwardingsTab/MaintenanceWorkflowForwardingShowLoading";

const MaintenanceWorkflowsShow = () => {
  const { id, "*": currentTabValue } = useParams();
  const navigate = useNavigate();

  const {
    init,
    setEquipment,
    setCurrentEquipmentUsage,
  } = useWorkflowShowStore();

  const {
    data: workflow,
    isPending: workflowIsPending,
    error: workflowError = {} as any,
    refetch: refetchWorkflow,
    dataUpdatedAt: workflowUpdatedAt,
  } = useMaintenanceWorkflow(id);

  const {
    data: forwardingsDirectiveEntryKlasses,
    isPending: forwardingsDirectiveEntryKlassesIsPending,
  } = useMaintenanceWorkflowForwardingsDirectiveEntryKlasses(id);

  const isScreenLoading = useMemo(
    () => workflowIsPending || forwardingsDirectiveEntryKlassesIsPending,
    [workflowIsPending, forwardingsDirectiveEntryKlassesIsPending],
  );

  const isCurrentForwardingTab = currentTabValue === workflow?.current_forwarding?.directive_entry_type;

  const ActionsComponent = useMemo(() => {
    if (!workflow?.current_forwarding?.status) {
      return null;
    }

    const action = maintenanceWorkflowActions[workflow?.current_forwarding?.status];

    return action?.component || null;
  }, [workflow?.current_forwarding?.status]);

  const tabsList = useMemo(
    () => getWorkflowTabsList(workflow, forwardingsDirectiveEntryKlasses),
    [workflow, forwardingsDirectiveEntryKlasses],
  );

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (workflow?.equipment) {
      setEquipment(workflow.equipment);
    }

    if (workflow?.equipment_usage_declaration) {
      setCurrentEquipmentUsage(workflow.equipment_usage_declaration.value);
    }
  }, [workflowUpdatedAt]);

  useEffect(() => {
    if (!workflow) return;

    if (tabsList.some((tab) => tab.value === currentTabValue)) return;

    const targetTab = workflow.current_forwarding?.directive_entry_type ?? '';

    navigate(
      targetTab
        ? `/maintenance-workflows/${id}/${targetTab}`
        : `/maintenance-workflows/${id}`,
      { replace: true },
    );
  }, [workflow, tabsList, currentTabValue, id, navigate]);

  return (
    <div className="inner-screen">
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/maintenance-forwardings`, searchLastRouteFromFallback: true }}
        isLoading={workflowIsPending}
        title="Encaminhamentos de manutenção"
        subtitle={workflow?.protocol_number}
        titleClassName="text-md"
        className="text-white-400"
        subtitleClassName="text-nowrap font-medium"
        buttons={isCurrentForwardingTab && ActionsComponent && <ActionsComponent />}
      />

      <ErrorMessageWrapper
        statusCode={workflowError?.status}
        refetchFunction={refetchWorkflow}
        className="flex-1"
      >
        <div className="flex-1 flex overflow-hidden gap-4">
          <MaintenanceWorkflowSidebar workflow={workflow} />

          <div className="flex-1 flex flex-col overflow-auto">
            {isScreenLoading && <MaintenanceWorkflowForwardingShowLoading />}

            {!isScreenLoading && (
              <Tabs
                {...tabsDefaultWithFlexGrowClassName}
                tabsList={tabsList}
              />
            )}
          </div>
        </div>
      </ErrorMessageWrapper>
    </div>
  );
}

export default MaintenanceWorkflowsShow;
